import { ECreditApplicationStatus, EDecision, EFinancingProgram } from '@if/core/types'
import { CreditApplicationProvider } from '@src/contexts/CreditApplicationContext'
import { useGetCreditApplicationLock } from '@src/data/api/credit-application/credit-application-api'
import { useGetFullCreditApplication } from '@src/data/api/full-credit-application/full-credit-application-api'
import { useEffect, useRef, useState } from 'react'
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import CreditApplicationMessagePage from '../CreditApplicationMessagePage'
import CreditDecisionPage from '../CreditDecisionPage'
import EditAutoRslaWorksheetPage from '../EditAutoRslaWorksheetPage'
import EditAutoWorksheetPage from '../EditAutoWorksheetPage'
import EditCreditApplicationPage from '../EditCreditApplicationPage/EditCreditApplicationPage'
import EditCreditRslaWorksheetPage from '../EditCreditRslaWorksheetPage'
import EditB2cWorksheetPage from '../EditWorksheet/EditB2cWorksheetPage'
import EditIfXpressWorksheetPage from '../EditWorksheet/EditIfXpressWorksheetPage'
import EditMechanicalWarrantyWorksheetPage from '../EditWorksheet/EditMechanicalWarrantyWorksheetPage/MechanicalWarranty'
import EditPersonalLoanWorksheetPage from '../EditWorksheet/EditPersonalLoanWorksheetPage'
import EditProductsWorksheetPage from '../EditWorksheet/EditProductsWorksheetPage'
import { FraudFlagsPage } from '../FraudFlagsPage'
import SplitDocuments from '../SplitDocsPage'
import ViewCreditApplicationPage from '../ViewCreditApplicationPage'
import B2cWorksheetViewPage from '../ViewWorksheet/B2cWorksheetViewPage'
import IfXpressWorksheetViewPage from '../ViewWorksheet/IfXpressWorksheetViewPage'
import MechanicalWarrantyWorksheetViewPage from '../ViewWorksheet/MechanicalWarrantyViewPage'
import PersonalLoanWorksheetViewPage from '../ViewWorksheet/PersonalLoanWorksheetViewPage'
import ProductsWorksheetViewPage from '../ViewWorksheet/ProductsWorksheetViewPage'
import WorksheetDecisionPage from '../WorksheetDecisionPage'
import RouteGuard from './RouteGuard'

const MAX_POLL_ATTEMPTS = 5

const LoadCreditApplication = () => {
  const navigate = useNavigate()
  const path = useLocation()
  const { id, financingProgramId } = useParams<{ id: string; financingProgramId: EFinancingProgram }>()

  // Poll count tracking for limiting polling attempts
  const pollCountRef = useRef(0)
  const [, forceUpdate] = useState({})

  useGetCreditApplicationLock(financingProgramId, id)

  const [fullCreditApp, isLoadingFullCreditApp, isErrorFullCreditApp, refetchFullCreditApp] = useGetFullCreditApplication(
    id,
    financingProgramId,
    pollCountRef.current > 0 && pollCountRef.current < MAX_POLL_ATTEMPTS,
  )
  const creditApplication = fullCreditApp?.credit

  // Determine if we should poll (refetch at interval)
  const pollPrequal =
    creditApplication?.prequalificationDecision === null &&
    creditApplication?.status === ECreditApplicationStatus.Active &&
    creditApplication.consentSoftHit &&
    !creditApplication.consentHardHit
  const pollFinalDecision =
    creditApplication?.finalCreditDecision?.decision === EDecision.Pending &&
    creditApplication?.status === ECreditApplicationStatus.Active &&
    creditApplication.consentSoftHit &&
    creditApplication.consentHardHit
  const shouldPoll = !!(creditApplication && (pollPrequal || pollFinalDecision))

  // Manage polling lifecycle
  useEffect(() => {
    if (shouldPoll && pollCountRef.current === 0) {
      // Start polling - set count to 1 to enable refetchInterval
      pollCountRef.current = 1
      forceUpdate({})
    } else if (!shouldPoll && pollCountRef.current > 0) {
      // Stop polling - decision received or conditions no longer met
      pollCountRef.current = 0
      forceUpdate({})
    }
  }, [shouldPoll])

  // Increment poll count on each refetch while polling is active
  useEffect(() => {
    if (shouldPoll && pollCountRef.current > 0 && pollCountRef.current < MAX_POLL_ATTEMPTS) {
      pollCountRef.current += 1
      if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
        // Force re-render to stop polling
        forceUpdate({})
      }
    }
  }, [fullCreditApp, shouldPoll])

  // Handle navigation on error
  useEffect(() => {
    if (isErrorFullCreditApp) {
      navigate('/notfound', { replace: true })
    }
  }, [isErrorFullCreditApp, navigate])

  document.title = `#${creditApplication?.referenceNumber ?? ''}`

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [path])

  // Don't render until we have required params
  if (!id || !financingProgramId) {
    return null
  }

  return (
    <CreditApplicationProvider
      value={{ fullCreditApp, isLoading: isLoadingFullCreditApp, isError: isErrorFullCreditApp, refetch: refetchFullCreditApp, id, financingProgramId }}
    >
      <Routes>
      <Route path="view" element={<ViewCreditApplicationPage />} />
      <Route element={<RouteGuard requiredPermission="canEditCreditApp" />}>
        <Route path="edit" element={<EditCreditApplicationPage />} />
      </Route>
      <Route path="decision" element={<CreditDecisionPage />} />
      <Route path="messages" element={<CreditApplicationMessagePage />} />
      <Route path="split-documents" element={<SplitDocuments />} />
      <Route path="fraud-flags" element={<FraudFlagsPage />} />
      {financingProgramId === EFinancingProgram.Auto && (
        <>
          <Route path="worksheet" element={<WorksheetDecisionPage />} />
          <Route path="worksheet/edit" element={<EditAutoWorksheetPage />} />
        </>
      )}

      {financingProgramId === EFinancingProgram.Personal && (
        <>
          <Route path="worksheet" element={<PersonalLoanWorksheetViewPage />} />
          <Route path="worksheet/edit" element={<EditPersonalLoanWorksheetPage />} />
        </>
      )}

      {financingProgramId === EFinancingProgram.B2c && (
        <>
          <Route path="worksheet" element={<B2cWorksheetViewPage />} />
          <Route path="worksheet/edit" element={<EditB2cWorksheetPage />} />
        </>
      )}

      {financingProgramId === EFinancingProgram.Products && (
        <>
          <Route path="worksheet" element={<ProductsWorksheetViewPage />} />
          <Route path="worksheet/edit" element={<EditProductsWorksheetPage />} />
        </>
      )}

      {financingProgramId === EFinancingProgram.MechanicalWarranty && (
        <>
          <Route path="worksheet" element={<MechanicalWarrantyWorksheetViewPage />} />
          <Route path="worksheet/edit" element={<EditMechanicalWarrantyWorksheetPage />} />
        </>
      )}

      {financingProgramId === EFinancingProgram.AutoRsla && (
        <Route path="worksheet/edit" element={<EditAutoRslaWorksheetPage />} />
      )}
      {financingProgramId === EFinancingProgram.CreditRsla && (
        <Route path="worksheet/edit" element={<EditCreditRslaWorksheetPage />} />
      )}
      {financingProgramId === EFinancingProgram.IfXpress && (
        <>
          <Route path="worksheet" element={<IfXpressWorksheetViewPage />} />
          <Route path="worksheet/edit" element={<EditIfXpressWorksheetPage />} />
        </>
      )}
      </Routes>
    </CreditApplicationProvider>
  )
}

export default LoadCreditApplication
